/* GoonQuiz engine. Pure functions, no DOM. Works in the browser (window.GoonQuiz) and in Node.
 *
 * Model
 *  1. Each answer yields evidence {trait, v, w}: a value v in 0..1 and a weight w.
 *  2. Trait estimate (shrunk toward 0.5 by a prior of weight 1):
 *         est_i  = (1*0.5 + sum(w*v)) / (1 + sum(w))          certainty_i = sum(w) / (1 + sum(w))
 *  3. Each persona k has target T_ki and importance W_ki. Weighted distance, using only traits we have evidence for:
 *         d2_k   = sum_i(W_ki*cert_i*(est_i - T_ki)^2) / (1 + sum_i(W_ki*cert_i))
 *         L_k    = exp(-d2_k / (2*sigma^2)),   P_k = L_k / sum_j L_j                      (softmax blend, sigma = 0.14)
 *         confidence = 1 - H(P)/ln(n)                                                      (normalised entropy)
 *  4. Surprise of an answer o, before it is added (how much it contradicts what we believed):
 *         fit_k(o)  = exp(-sum(w*W_ki*(v-T_ki)^2)/sum(w*W_ki) / (2*sigma^2))
 *         surprise  = 1 - (sum_k P_k*fit_k(o)) / max_k fit_k(o)
 *     Interludes fire from surprise, patterns (flip-flops, extremes, streaks) and timing. They are rationed: at most 4,
 *     none before answer 4, at least 3 answers apart, and each kind only once.
 *  5. After the core questions the engine asks up to 4 extra questions, picking the one that best separates the two
 *     leading personas: score(q) = mean over options |fit_top1(o) - fit_top2(o)|.
 *  6. Gooner Quotient = round(100 * (0.40*E + 0.25*M + 0.20*V + 0.15*max(S, X))).
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory(require('./goon-quiz-data.js'));
  else root.GoonQuiz = factory(root.GoonQuizData);
})(typeof self !== 'undefined' ? self : this, function (D) {
  const TUNE = Object.assign({ sigma: 0.14, earlyP: 0.35, earlyGap: 0.18, minCards: 8 }, D.TUNING || {});
  const SIGMA = TUNE.sigma, PRIOR_W = 1, MAX_INTERLUDES = 3, MIN_GAP = 3, MAX_ADAPTIVE = 3, CORE_MIN = TUNE.minCards;
  const TRAITS = D.TRAITS, PERSONAS = D.PERSONAS;

  function mulberry32(a) { return function () { a |= 0; a = a + 0x6D2B79F5 | 0; let t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }

  function evidenceOf(q, answer) {
    const out = [];
    if (q.type === 'choice') { const o = q.options[answer]; Object.keys(o.ev).forEach(t => out.push({ trait: t, v: o.ev[t][0], w: o.ev[t][1] })); }
    else if (q.type === 'swipe') { const e = answer ? q.yes : q.no; Object.keys(e).forEach(t => out.push({ trait: t, v: e[t][0], w: e[t][1] })); }
    else if (q.type === 'multi') { (answer || []).slice(0, 2).forEach(i => { const o = q.options[i]; Object.keys(o.ev).forEach(t => out.push({ trait: t, v: o.ev[t][0], w: o.ev[t][1] })); }); }
    else if (q.type === 'slider') out.push({ trait: q.trait, v: Math.max(0, Math.min(100, Number(answer))) / 100, w: q.weight });
    else if (q.type === 'likert') q.items.forEach((it, i) => { const raw = (Math.max(1, Math.min(5, Number(answer[i])) ) - 1) / 4; out.push({ trait: it.trait, v: it.dir < 0 ? 1 - raw : raw, w: 1.5 }); });
    return out;
  }

  function estimate(evidence) {
    const est = {};
    TRAITS.forEach(t => { let sw = 0, sv = 0; evidence.forEach(e => { if (e.trait === t) { sw += e.w; sv += e.w * e.v; } }); est[t] = { v: (PRIOR_W * 0.5 + sv) / (PRIOR_W + sw), cert: sw / (PRIOR_W + sw), w: sw }; });
    return est;
  }

  function classify(est) {
    const L = PERSONAS.map(p => { let num = 0, den = 0; TRAITS.forEach(t => { const wt = (p.W[t] || .25) * est[t].cert; num += wt * Math.pow(est[t].v - p.T[t], 2); den += wt; }); return Math.exp(-(num / (1 + den)) / (2 * SIGMA * SIGMA)); });
    const sum = L.reduce((a, b) => a + b, 0), probs = L.map(x => x / sum);
    const H = -probs.reduce((a, p) => a + (p > 0 ? p * Math.log(p) : 0), 0);
    const ranked = PERSONAS.map((p, i) => ({ id: p.id, name: p.name, p: probs[i], persona: p })).sort((a, b) => b.p - a.p);
    return { probs, ranked, confidence: 1 - H / Math.log(PERSONAS.length) };
  }

  function fits(evList) {
    return PERSONAS.map(p => { let num = 0, den = 0; evList.forEach(e => { const wt = e.w * (p.W[e.trait] || .25); num += wt * Math.pow(e.v - p.T[e.trait], 2); den += wt; }); return den > 0 ? Math.exp(-(num / den) / (2 * SIGMA * SIGMA)) : 1; });
  }
  function surprise(evList, probs) {
    const f = fits(evList), mx = Math.max.apply(null, f); if (!(mx > 0)) return 0;
    return 1 - f.reduce((a, x, i) => a + probs[i] * x, 0) / mx;
  }

  function createSession(opts) {
    const seed = (opts && opts.seed != null) ? opts.seed : Math.floor(Math.random() * 1e9);
    return { seed, rng: mulberry32(seed), idx: 0, asked: [], evidence: [], topHistory: [], interludes: [], lastInterludeAt: -99, adaptiveAsked: 0, finished: false };
  }

  function candidateScore(q, top1, top2) {
    const ev2 = e => Object.keys(e).map(t => ({ trait: t, v: e[t][0], w: e[t][1] }));
    const opts = q.type === 'swipe' ? [ev2(q.yes), ev2(q.no)] : q.type === 'slider' ? [[{ trait: q.trait, v: .1, w: q.weight }], [{ trait: q.trait, v: .9, w: q.weight }]]
      : q.options.map(o => Object.keys(o.ev).map(t => ({ trait: t, v: o.ev[t][0], w: o.ev[t][1] })));
    return opts.reduce((a, ev) => { const f = fits(ev); return a + Math.abs(f[top1] - f[top2]); }, 0) / opts.length;
  }

  function nextQuestion(s) {
    if (s.idx < D.CORE.length) {
      if (s.idx >= CORE_MIN) {           // early finish: after 8 cards, stop as soon as one persona clearly leads
        const c0 = classify(estimate(s.evidence));
        if (c0.ranked[0].p >= TUNE.earlyP && c0.ranked[0].p - c0.ranked[1].p >= TUNE.earlyGap) { s.finished = true; return null; }
      }
      return D.CORE[s.idx];
    }
    if (s.adaptiveAsked >= MAX_ADAPTIVE) { s.finished = true; return null; }
    const cls = classify(estimate(s.evidence));
    if (cls.confidence >= 0.55 && cls.ranked[0].p - cls.ranked[1].p >= 0.2) { s.finished = true; return null; }
    const i1 = PERSONAS.findIndex(p => p.id === cls.ranked[0].id), i2 = PERSONAS.findIndex(p => p.id === cls.ranked[1].id);
    const asked = new Set(s.asked.map(a => a.id));
    let best = null, bs = 0.05;
    D.ADAPTIVE.forEach(q => { if (asked.has(q.id)) return; const sc = candidateScore(q, i1, i2); if (sc > bs) { bs = sc; best = q; } });
    if (!best) { s.finished = true; return null; }
    return best;
  }

  const kindShown = (s, k) => s.interludes.some(i => i.kind === k);
  const pickLine = (s, kind, top) => { const pool = D.INTERLUDES[kind]; return pool[Math.floor(s.rng() * pool.length)].split('{top}').join(top); };
  function traitHas(s, trait, cond) { return s.evidence.filter(e => e.trait === trait && cond(e)); }

  function chooseInterlude(s, ctx) {
    const n = s.asked.length, top = ctx.topName;
    if (s.finished) return { kind: 'finale', text: pickLine(s, 'finale', top) };
    if (n < 4 || s.interludes.filter(i => i.kind !== 'finale').length >= MAX_INTERLUDES || n - s.lastInterludeAt < MIN_GAP) return null;
    const est = ctx.est, cls = ctx.cls, last2 = s.asked.slice(-2);
    const rules = [
      ['contradiction', () => ctx.surprise >= 0.5 && ctx.priorTopP >= 0.3 && !kindShown(s, 'contradiction')],
      ['reveal', () => cls.confidence >= 0.3 && n >= 5 && !kindShown(s, 'reveal')],
      ['splitRole', () => traitHas(s, 'R', e => e.v < .3 && e.w >= 1).length > 0 && traitHas(s, 'R', e => e.v > .7 && e.w >= 1).length > 0 && !kindShown(s, 'splitRole')],
      ['controlFlip', () => traitHas(s, 'P', e => e.v < .25 && e.w >= 2).length > 0 && traitHas(s, 'P', e => e.v > .75 && e.w >= 2).length > 0 && !kindShown(s, 'controlFlip')],
      ['loyalNovel', () => traitHas(s, 'V', e => e.v < .2 && e.w >= 2).length > 0 && traitHas(s, 'V', e => e.v > .8 && e.w >= 2).length > 0 && !kindShown(s, 'loyalNovel')],
      ['watchedSolo', () => est.X.v >= .7 && est.X.cert >= .4 && est.S.v <= .3 && est.S.cert >= .4 && !kindShown(s, 'watchedSolo')],
      ['extreme', () => last2.length === 2 && TRAITS.some(t => last2.every(a => a.ev.some(e => e.trait === t && e.w >= 2 && (e.v >= .9 || e.v <= .1)))) && !kindShown(s, 'extreme')],
      ['streak', () => s.topHistory.length >= 4 && s.topHistory.slice(-4).every(x => x.id === s.topHistory[s.topHistory.length - 1].id && x.p >= .4) && !kindShown(s, 'streak')],
      ['slow', () => ctx.ms >= 9000 && !kindShown(s, 'slow')],
      ['fast', () => s.asked.slice(-3).length === 3 && s.asked.slice(-3).every(a => a.ms > 0 && a.ms <= 1000) && !kindShown(s, 'fast')]
    ];
    for (const r of rules) if (r[1]()) return { kind: r[0], text: pickLine(s, r[0], top) };
    return null;
  }

  function submit(s, qid, answer, ms) {
    const q = D.CORE.concat(D.ADAPTIVE).find(x => x.id === qid);
    if (!q) throw new Error('unknown question ' + qid);
    const ev = evidenceOf(q, answer);
    const priorEst = estimate(s.evidence), priorCls = classify(priorEst);
    const sur = s.asked.length >= 3 ? surprise(ev, priorCls.probs) : 0;
    s.evidence = s.evidence.concat(ev);
    s.asked.push({ id: qid, ev, ms: ms || 0, answer });
    if (qid.charAt(0) === 'a') s.adaptiveAsked++; else s.idx++;
    const est = estimate(s.evidence), cls = classify(est);
    s.topHistory.push({ id: cls.ranked[0].id, p: cls.ranked[0].p });
    const more = nextQuestion(s);       // sets s.finished when nothing is left to ask
    const il = chooseInterlude(s, { est, cls, surprise: sur, priorTopP: priorCls.ranked[0].p, ms: ms || 0, topName: cls.ranked[0].name });
    if (il) { s.interludes.push({ kind: il.kind, at: s.asked.length }); if (il.kind !== 'finale') s.lastInterludeAt = s.asked.length; }
    return { interlude: il, next: more, done: !more, debug: { surprise: sur, confidence: cls.confidence, top: cls.ranked.slice(0, 3).map(r => [r.name, +r.p.toFixed(3)]) } };
  }

  function result(s) {
    const est = estimate(s.evidence), cls = classify(est), v = {}; TRAITS.forEach(t => { v[t] = est[t].v; });
    const gq = Math.round(100 * (0.40 * v.E + 0.25 * v.M + 0.20 * v.V + 0.15 * Math.max(v.S, v.X)));
    const band = D.BANDS.find(b => gq >= b[0] && gq < b[1])[2];
    const second = cls.ranked[1].p >= 0.2 ? cls.ranked[1] : null;
    return {
      persona: cls.ranked[0].persona, secondary: second && second.persona, confidence: cls.confidence,
      blend: cls.ranked.filter(r => r.p >= 0.05).slice(0, 4).map(r => ({ id: r.id, name: r.name, pct: Math.round(r.p * 100) })),
      traits: v, certainty: Object.fromEntries(TRAITS.map(t => [t, est[t].cert])),
      role: v.R >= .65 ? 'Top' : v.R <= .35 ? 'Bottom' : 'Vers', topPct: Math.round(100 * v.R),
      ranking: cls.ranked.map(r => ({ id: r.id, p: r.p })),
      gq, band, needsBalanceNote: v.E >= 0.85, interludesShown: s.interludes.length
    };
  }

  // ---- Bag selection -------------------------------------------------------------------------------------------
  // The persona picks the bag; the user's own traits pick the variant; stock decides whether it can be sold.
  //   1. Walk the personas from most to least likely. For each, take the first variant whose `when` conditions the user's
  //      trait estimates satisfy (the variant without `when` is the default), then any other variant of the same bag.
  //   2. A variant is only offered if it passed verification (ok) and at least one bag is in stock; otherwise fall to the
  //      next persona and record who we fell back from.
  //   3. Add-on: the best-fitting single item from the runner-up persona's bag that is not already in the chosen bag.
  const CAT_AFFINITY = {   // how much each product category suits a trait profile (0..1)
    stroker: t => t.R, sleeve: t => t.R, ring: t => .4 + .4 * t.R, egg: t => .5 + .3 * t.V,
    prostate: t => 1 - t.R, plug: t => 1 - t.R, prep: t => 1 - t.R,
    restraint: t => .3 + .7 * Math.abs(t.P - .5) * 2, impact: t => t.P, rope: t => t.P, clamps: t => .4 + .4 * (1 - t.P),
    blindfold: t => 1 - t.P, gag: t => 1 - t.P, chastity: t => (1 - t.P) * .6 + t.E * .4,
    harness: t => t.X, glow: t => t.X, pasties: t => t.X, game: t => t.S, lube: () => .5, cleaner: () => .4
  };
  const cmp = { '>=': (a, b) => a >= b, '<=': (a, b) => a <= b, '>': (a, b) => a > b, '<': (a, b) => a < b };
  const whenOk = (when, traits) => !when || Object.keys(when).every(t => cmp[when[t][0]](traits[t], when[t][1]));

  function chooseBag(res, config, opts) {
    const minAvail = (opts && opts.minAvailable) || 1, traits = res.traits;
    const byPersona = {}; config.bags.forEach(b => { byPersona[b.persona] = b; });
    let primary = null, fellBackFrom = null;
    for (let k = 0; k < res.ranking.length && !primary; k++) {
      const bag = byPersona[res.ranking[k].id]; if (!bag) continue;
      const usable = bag.variants.filter(v => v.ok && v.available >= minAvail);
      const pick = usable.find(v => v.when && whenOk(v.when, traits)) || usable.find(v => !v.when) || usable[0];
      if (pick) primary = { bag, variant: pick, personaId: res.ranking[k].id };
      else if (!fellBackFrom) fellBackFrom = res.ranking[k].id;
    }
    if (!primary) return null;
    const have = new Set(primary.variant.items.map(x => x.handle)), haveCats = new Set(primary.variant.items.map(x => x.cat)); let addon = null, best = -1;
    const runnerUp = res.ranking.map(r => r.id).find(id => id !== primary.personaId && byPersona[id]);
    if (runnerUp) byPersona[runnerUp].variants.filter(v => v.ok && v.available >= minAvail).forEach(v => v.items.forEach(it => {
      if (have.has(it.handle) || haveCats.has(it.cat) || it.cat === 'lube' || it.cat === 'cleaner') return;   // never two items of one category
      // an add-on is an easy extra: it must be cheap (<= $30) and clearly suit this user (affinity >= .55)
      const f = CAT_AFFINITY[it.cat] ? CAT_AFFINITY[it.cat](traits) : .3; if (it.price <= 30 && f >= .55 && f > best) { best = f; addon = it; }
    }));
    const sortItems = list => list.slice().sort((a, b) => (CAT_AFFINITY[b.cat] ? CAT_AFFINITY[b.cat](traits) : 0) - (CAT_AFFINITY[a.cat] ? CAT_AFFINITY[a.cat](traits) : 0));
    const pv = primary.variant.pro, pro = pv && pv.ok && pv.available >= minAvail ? { items: sortItems(pv.items), retail: pv.retail, price: pv.price, savePct: pv.savePct, savings: pv.savings } : null;
    return { personaId: primary.personaId, name: primary.bag.name, pro, gift: config.gift || null, variantId: primary.variant.id, variantLabel: primary.variant.label,
      items: sortItems(primary.variant.items),
      retail: primary.variant.retail, price: primary.variant.price, savePct: primary.variant.savePct, savings: primary.variant.savings,
      fellBackFrom, addon: addon && { handle: addon.handle, title: addon.title, price: addon.price, variantId: addon.variantId, cat: addon.cat, from: runnerUp } };
  }
  // opts: { pro: true } for the Pro bag, { addon: true } adds the add-on, { gift: true } adds the free sachet (the shop's gift discount makes it 0)
  function cartLink(choice, config, opts) {
    if (opts === true) opts = { addon: true };   // older callers
    opts = opts || {};
    const list = opts.pro && choice.pro ? choice.pro.items : choice.items;
    const lines = list.map(x => x.variantId + ':' + x.qty); if (opts.addon && choice.addon) lines.push(choice.addon.variantId + ':1'); if (opts.gift && choice.gift) lines.push(choice.gift.variantId + ':1');
    return config.shopUrl + '/cart/' + lines.join(',') + '?utm_source=quiz&utm_medium=goonbag&utm_campaign=' + choice.personaId + (opts.pro ? '&utm_content=pro' : '');
  }

  return { createSession, nextQuestion, submit, result, chooseBag, cartLink, classify, estimate, evidenceOf, surprise, fits, constants: { SIGMA, MAX_INTERLUDES, MIN_GAP, MAX_ADAPTIVE } };
});
